/*
 * Copyright 2025 Kridaprema
 */
package com.karyadina.ui.theme

import androidx.compose.ui.graphics.Color

// Public primitives (Internal use only)
object StudyPalette {
    val Red500 = Color(0xFFB00020)
    val Red600 = Color(0xFFB3261E) // Error
    val Green500 = Color(0xFF2E7D32) // Brand Green (Student)
    val Blue500 = Color(0xFF1565C0) // Brand Blue (Professional)
    val Orange500 = Color(0xFFEF6C00) // Brand Orange (Developer)
    val Purple500 = Color(0xFF9C27B0) // Brand Purple (Creator)
    val Cyan500 = Color(0xFF00838F) // Brand Cyan (Researcher)
    val Yellow500 = Color(0xFFF2C94C)

    // Light Mode Backgrounds (Pastels)
    val Blue50 = Color(0xFFE3F2FD)
    val Green50 = Color(0xFFE8F5E9)
    val Yellow50 = Color(0xFFFFF9C4)
    val Orange50 = Color(0xFFFFF3E0)
    val Purple50 = Color(0xFFF3E5F5)
    val Cyan50 = Color(0xFFE0F7FA)

    // Dark Mode Backgrounds (Deep, muted, translucent-feel)
    // These are darker versions of the above to sit nicely on a dark grey surface
    val BlueDark = Color(0xFF1E2A38)
    val GreenDark = Color(0xFF1E3023)
    val YellowDark = Color(0xFF332D18)
    val OrangeDark = Color(0xFF36291C)
    val PurpleDark = Color(0xFF2D1F30)
    val CyanDark = Color(0xFF1A3036)

    val Red200 = Color(0xFFF2B8B5)
    val Green200 = Color(0xFF8AD8B6)
    val Warning500 = Color(0xFFD32F2F)  // Material Warning Red
    val Warning50 = Color(0xFFFFEBEE)   // Light warning background
    val WarningDark = Color(0xFFCF6679) // Dark mode warning

    // Flashcard Mastery Level Colors
    // NEW State (Gray - Not started)
    val MasteryNew500 = Color(0xFFB0BEC5)  // Light gray
    val MasteryNew50 = Color(0xFFECEFF1)   // Extra light background
    val MasteryNewDark = Color(0xFF455A64) // Dark gray for dark theme
    
    // LEARNING State (Orange - In progress)
    val MasteryLearning500 = Color(0xFFFFB74D)  // Amber/Orange
    val MasteryLearning50 = Color(0xFFFFF3E0)  // Extra light orange background
    val MasteryLearningDark = Color(0xFFE65100) // Dark orange for dark theme
    
    // REVIEW State (Blue - Review scheduled)
    val MasteryReview500 = Color(0xFF2196F3)  // Material Blue
    val MasteryReview50 = Color(0xFFE3F2FD)  // Extra light blue background
    val MasteryReviewDark = Color(0xFF0D47A1) // Dark blue for dark theme
    
    // MASTERED State (Green - Fully mastered)
    val MasteryMastered500 = Color(0xFF4CAF50)  // Material Green
    val MasteryMastered50 = Color(0xFFC8E6C9)   // Light green
    val MasteryMasteredDark = Color(0xFF2E7D32) // Dark green for dark theme
    
    // FAMILIAR State (Light Green - Partially known)
    val MasteryFamiliar500 = Color(0xFF81C784)  // Light green
    val MasteryFamiliar50 = Color(0xFFF1F8E9)   // Extra light background
    val MasteryFamiliarDark = Color(0xFF558B2F) // Dark font green for dark theme
    
    // KNOWN State (Dark Green - Well-known)
    val MasteryKnown500 = Color(0xFF689F38)  // Deep green
    val MasteryKnown50 = Color(0xFFF1F8E9)   // Extra light background
    val MasteryKnownDark = Color(0xFF33691E) // Dark font green for dark theme

    // Dark Mode Gradients
    val Teal900 = Color(0xFF004D40)
    val Cyan900 = Color(0xFF006064)
}

// Public Semantic Colors
object StudyColors {
    val ActiveLight = StudyPalette.Red500
    val ActiveDark = StudyPalette.Red200

    val InProgressLight = StudyPalette.Yellow500
    val InProgressDark = StudyPalette.Yellow50

    val CompletedLight = StudyPalette.Green500
    val CompletedDark = StudyPalette.Green200
    
    val WarningLight = StudyPalette.Warning500
    val WarningDark = StudyPalette.WarningDark

    // Light Mode Palette
    val PaletteLight = listOf(
        StudyPalette.Blue50,
        StudyPalette.Green50,
        StudyPalette.Orange50,
        StudyPalette.Purple50,
        StudyPalette.Cyan50,
        StudyPalette.Yellow50
    )

    // Dark Mode Palette
    val PaletteDark = listOf(
        StudyPalette.BlueDark,
        StudyPalette.GreenDark,
        StudyPalette.OrangeDark,
        StudyPalette.PurpleDark,
        StudyPalette.CyanDark,
        StudyPalette.YellowDark
    )

    // Maintain backward compatibility if used elsewhere
    val Palette = PaletteLight
}
